import type { AuthSession, PublicConfig, User } from "../types";

const API = "/api";

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string,
): Promise<T> {
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `HTTP ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  login: (user: string, pin: string) =>
    request<AuthSession>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ user, pin }),
    }),
  config: (token: string) => request<PublicConfig>("/config", {}, token),
  users: (token: string) => request<User[]>("/users", {}, token),
  adminUsers: (token: string) => request<User[]>("/admin/users", {}, token),
  createUser: (
    token: string,
    user: { id: string; display_name: string; pin: string },
  ) =>
    request<User>(
      "/admin/users",
      { method: "POST", body: JSON.stringify(user) },
      token,
    ),
  updateUser: (
    token: string,
    userId: string,
    update: Partial<{ display_name: string; pin: string; is_active: boolean }>,
  ) =>
    request<User>(
      `/admin/users/${encodeURIComponent(userId)}`,
      { method: "PATCH", body: JSON.stringify(update) },
      token,
    ),
  activeCall: (token: string) =>
    request<{ call: null | { id: string; callerId: string; calleeId: string; status: string; peerId: string; peerName: string } }>(
      "/calls/active",
      {},
      token,
    ),
  createCall: (token: string, targetUserId: string) =>
    request<{ callId: string }>(
      "/calls",
      { method: "POST", body: JSON.stringify({ target_user_id: targetUserId }) },
      token,
    ),
  callAction: (token: string, callId: string, action: "accept" | "decline" | "hangup") =>
    request<{ status: string }>(`/calls/${callId}/${action}`, { method: "POST" }, token),
  saveSubscription: (token: string, subscription: PushSubscription) =>
    request<void>(
      "/push/subscriptions",
      { method: "POST", body: JSON.stringify(subscription.toJSON()) },
      token,
    ),
};

export function urlBase64ToUint8Array(value: string): Uint8Array {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}
